import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { KW_PER_PANEL, createUserBatterySolarDetails } from '../api/Optimise';
import awsconfig from '../config/aws-exports';
import { WELCOME_MODAL_PENDING_KEY } from '../constants/onboarding';
import { AppContext } from '../context/AppContext';
import { OnboardingContext } from '../context/OnboardingContext';
import type { UserBatterySolarDetails } from '../types/optimise';
import getBatteryMaxDischargePower from '../utils/getBatteryMaxDischargePower';
import prepareArrays from '../utils/prepareArrays';
import { getFinalExportTariffId } from '../utils/tariffUtils';
import yesNoToBool from '../utils/yesNoToBool';

/**
 * Interface for battery detail structure
 */
interface BatteryDetail {
  manufacturer: string;
  model: string;
  quantity: number;
}

/**
 * Interface for inverter detail structure
 */
interface InverterDetail {
  unit_name?: string;
}

/**
 * Interface for hardware details structure returned from assembleHardwareDetails
 */
interface HardwareDetails {
  batteryCapacity: number;
  batteryDegradationCost: number;
  batteryEfficiency: number;
  batteryMaxChargePower: number;
  batteryMaxDischargePower: number;
  inverterDetails: InverterDetail;
  batteryDetails: BatteryDetail[];
}

/**
 * Interface for solar panel array data structure
 */
interface SolarPanelArrayItem {
  orientation: string | number;
  tilt: string | number;
  panelCount: string | number;
}

interface SolarPanelArrayData {
  [key: string]: SolarPanelArrayItem;
}

/**
 * Partial interface for OnboardingContext values used in this hook
 */
interface OnboardingContextValues {
  postcode: string | null;
  importTariffId: string | null;
  exportTariffId: string | null;
  exportTariffs: import('../types/tariff').Tariff[] | null;
  hasEv: string | null;
  hasSolarDiverter: string | null;
  pvSystemSize: number | null;
  numPanels: number | null;
  solarPanelArrayData: SolarPanelArrayData | null;
  inverterManufacturer: string | null;
  hasExportLimit: string | null;
  hardwareDetails: HardwareDetails | null;
  hasSmartTariff: string | null;
}

/**
 * Partial interface for AppContext values used in this hook
 */
interface AppContextValues {
  setIsOnboarded: (value: boolean) => void;
}

/**
 * Return type for useSetUserDetails hook
 */
interface UseSetUserDetailsReturn {
  triggerSetUserDetails: (next?: string) => Promise<void>;
  requestError: Error | undefined;
}

const useSetUserDetails = (): UseSetUserDetailsReturn => {
  const navigate = useNavigate();

  // Extract required context values
  const {
    postcode,
    importTariffId,
    exportTariffId,
    exportTariffs,
    hasEv,
    hasSolarDiverter,
    pvSystemSize,
    numPanels,
    solarPanelArrayData,
    inverterManufacturer,
    hasExportLimit,
    hardwareDetails,
    hasSmartTariff,
  } = useContext(OnboardingContext) as unknown as OnboardingContextValues;

  const { setIsOnboarded } = useContext(AppContext) as unknown as AppContextValues;

  const [requestError, setRequestError] = useState<Error | undefined>(undefined);

  const triggerSetUserDetails = useCallback(
    async (next: string = '/onboarding/success'): Promise<void> => {
      // Validate prerequisites without throwing
      if (!hardwareDetails) {
        setRequestError(new Error('Hardware details are required'));
        return;
      }

      if (!postcode) {
        setRequestError(new Error('Postcode is required'));
        return;
      }

      // Only if the user has a smart tariff
      if (yesNoToBool(hasSmartTariff) && !importTariffId) {
        setRequestError(new Error('Import tariff ID is required'));
        return;
      }

      try {
        const {
          batteryCapacity,
          batteryDegradationCost,
          batteryEfficiency,
          batteryMaxChargePower,
          batteryMaxDischargePower,
          inverterDetails,
          batteryDetails,
        } = hardwareDetails;

        const solarCapacityKw = pvSystemSize || (numPanels ? numPanels * KW_PER_PANEL : 0);

        // If the user is not on a smart tariff we default to null
        const finalExportTariffId = yesNoToBool(hasSmartTariff)
          ? await getFinalExportTariffId(exportTariffId || undefined, postcode, exportTariffs || undefined)
          : null;

        // Only if the user has a smart tariff
        if (yesNoToBool(hasSmartTariff) && !finalExportTariffId) {
          setRequestError(new Error('Export tariff ID is required'));
          return;
        }

        const details: UserBatterySolarDetails = {
          battery_capacity: batteryCapacity,
          battery_degradation_cost: batteryDegradationCost,
          battery_efficiency: batteryEfficiency,
          battery_max_charge_power: batteryMaxChargePower,
          battery_max_discharge_power: getBatteryMaxDischargePower(
            batteryMaxDischargePower,
            yesNoToBool(hasExportLimit || undefined)
          ),
          battery_soc_lower_limit: 10,
          battery_soc_upper_limit: 100,
          simulation_mode: yesNoToBool(hasSmartTariff) ? awsconfig.signup_simulation_mode : true,
          import_tariff_id: importTariffId,
          export_tariff_id: finalExportTariffId ?? null,
          postcode: postcode,
          hardware_details: {
            batteries: batteryDetails.map((battery) => ({
              manufacturer: battery.manufacturer,
              model: battery.model,
              quantity: String(battery.quantity),
            })),
            inverter: {
              manufacturer: inverterManufacturer || '',
              model: inverterDetails?.unit_name || '',
            },
          },
          pv_details: {
            solar_inverter_capacity_kw: solarCapacityKw,
            solar_module_capacity_kw: solarCapacityKw,
            arrays: await prepareArrays(solarPanelArrayData || undefined, postcode),
          },
          additional_information: {
            has_ev: yesNoToBool(hasEv || undefined) || false,
            has_solar_diverter: yesNoToBool(hasSolarDiverter || undefined) || false,
            has_smart_tariff: yesNoToBool(hasSmartTariff || undefined) || false,
          },
        };

        await createUserBatterySolarDetails(details);

        window.localStorage.setItem(WELCOME_MODAL_PENDING_KEY, JSON.stringify(true));
        setIsOnboarded(true);
        void navigate(next);
      } catch (err) {
        console.error(err);
        setRequestError(err instanceof Error ? err : new Error(String(err)));
      }
    },
    [
      hardwareDetails,
      postcode,
      importTariffId,
      exportTariffId,
      exportTariffs,
      hasEv,
      hasSolarDiverter,
      pvSystemSize,
      numPanels,
      solarPanelArrayData,
      inverterManufacturer,
      hasExportLimit,
      hasSmartTariff,
      setIsOnboarded,
      navigate,
    ]
  );

  return { triggerSetUserDetails, requestError };
};

export default useSetUserDetails;
